import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  Building2, TrendingUp, TrendingDown, AlertTriangle, 
  CheckCircle2, Clock, Plus, RefreshCw, FileText, 
  PoundSterling, Users, MapPin, Calendar, ArrowRight,
  BarChart3, PieChart, AlertCircle, Receipt, Percent, Edit2, Trash2
} from 'lucide-react';

// Use window.location.origin for same-origin requests to avoid mixed content issues
const getSecureAPI = () => {
  // In production, always use the current origin
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return window.location.origin;
  }
  // Fallback to env variable
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (envUrl.startsWith('http://') && typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return envUrl.replace('http://', 'https://');
  }
  return envUrl;
};

const TIER_COLORS = {
  'ESSENTIAL': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'STANDARD': 'bg-amber-100 text-amber-800 border-amber-300',
  'PREMIUM': 'bg-red-100 text-red-800 border-red-300',
};

const TIER_BG_COLORS = {
  'ESSENTIAL': 'from-emerald-500 to-emerald-600',
  'STANDARD': 'from-amber-500 to-amber-600',
  'PREMIUM': 'from-red-500 to-red-600',
};

const MARGIN_STATUS_COLORS = {
  'healthy': 'text-emerald-600',
  'warning': 'text-amber-600',
  'critical': 'text-red-600',
};

export default function ContractsDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [sites, setSites] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContract, setShowNewContract] = useState(false);
  const [showNewSite, setShowNewSite] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [calculatorUnits, setCalculatorUnits] = useState(100);
  const [calculatorTier, setCalculatorTier] = useState('STANDARD');
  const [calculatorPrice, setCalculatorPrice] = useState(350);
  const [calculatorResult, setCalculatorResult] = useState(null);
  
  // Client Markup States
  const [markups, setMarkups] = useState([]);
  const [defaultMarkup, setDefaultMarkup] = useState(25);
  const [showAddMarkup, setShowAddMarkup] = useState(false);
  const [newMarkupClient, setNewMarkupClient] = useState('');
  const [newMarkupPercent, setNewMarkupPercent] = useState(25);
  const [newMarkupNotes, setNewMarkupNotes] = useState('');
  const [editingMarkup, setEditingMarkup] = useState(null);
  
  const token = localStorage.getItem('token');

  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    const secureAPI = getSecureAPI();
    try {
      const [dashRes, tiersRes, contractsRes, sitesRes, renewalsRes, markupsRes] = await Promise.all([
        axios.get(`${secureAPI}/api/contracts/dashboard`, { headers }),
        axios.get(`${secureAPI}/api/contracts/tiers`, { headers }),
        axios.get(`${secureAPI}/api/contracts/?status=active&limit=50`, { headers }),
        axios.get(`${secureAPI}/api/contracts/sites?limit=50`, { headers }),
        axios.get(`${secureAPI}/api/contracts/renewals/upcoming?days=60`, { headers }),
        axios.get(`${secureAPI}/api/fm/markups`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setTiers(tiersRes.data.tiers || []);
      setContracts(contractsRes.data.contracts || []);
      setSites(sitesRes.data.sites || []);
      setRenewals(renewalsRes.data.renewals || []);
      setMarkups(markupsRes.data.markups || []);
      setDefaultMarkup(markupsRes.data.default_markup || 25);
    } catch (err) {
      console.error('Dashboard load error:', err);
      toast.error('Failed to load contracts dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const calculateContract = async () => {
    const secureAPI = getSecureAPI();
    try {
      const res = await axios.post(`${secureAPI}/api/contracts/calculate`, null, {
        params: { units: calculatorUnits, tier_id: calculatorTier, price_per_unit: calculatorPrice },
        headers
      });
      setCalculatorResult(res.data.financials);
    } catch (err) {
      toast.error('Calculation failed');
    }
  };

  // Markup CRUD Functions
  const handleAddMarkup = async () => {
    const secureAPI = getSecureAPI();
    if (!newMarkupClient.trim()) {
      toast.error('Client name is required');
      return;
    }
    
    try {
      await axios.post(`${secureAPI}/api/fm/markups`, {
        client_name: newMarkupClient,
        markup_percent: newMarkupPercent,
        notes: newMarkupNotes
      }, { headers });
      
      toast.success(`Markup of ${newMarkupPercent}% added for ${newMarkupClient}`);
      setShowAddMarkup(false);
      setNewMarkupClient('');
      setNewMarkupPercent(25);
      setNewMarkupNotes('');
      loadDashboard();
    } catch (err) {
      const errMsg = typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to add markup';
      toast.error(errMsg);
    }
  };

  const handleUpdateMarkup = async (markupId, newPercent) => {
    const secureAPI = getSecureAPI();
    try {
      await axios.patch(`${secureAPI}/api/fm/markups/${markupId}`, {
        markup_percent: parseFloat(newPercent)
      }, { headers });
      
      toast.success('Markup updated');
      setEditingMarkup(null);
      loadDashboard();
    } catch (err) {
      toast.error('Failed to update markup');
    }
  };

  const handleDeleteMarkup = async (markupId, clientName) => {
    const secureAPI = getSecureAPI();
    if (!window.confirm(`Remove custom markup for ${clientName}? They will use the default ${defaultMarkup}% markup.`)) {
      return;
    }
    
    try {
      await axios.delete(`${secureAPI}/api/fm/markups/${markupId}`, { headers });
      toast.success(`Custom markup removed for ${clientName}`);
      loadDashboard();
    } catch (err) {
      toast.error('Failed to delete markup');
    }
  };

  // New Site Form Component
  const NewSiteForm = () => {
    const secureAPI = getSecureAPI();
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
      client_id: '',
      name: '',
      address: '',
      postcode: '',
      region: '',
      units: 1,
      site_manager_name: '',
      site_manager_email: '',
      site_manager_phone: '',
    });

    useEffect(() => {
      axios.get(`${secureAPI}/api/clients?limit=100`, { headers })
        .then(res => setClients(res.data || []))
        .catch(() => {});
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${secureAPI}/api/contracts/sites`, formData, { headers });
        toast.success('Site created successfully');
        setShowNewSite(false);
        loadDashboard();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create site');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Client *</Label>
          <Select value={formData.client_id} onValueChange={v => setFormData({...formData, client_id: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label>Site Name *</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Seaside Holiday Park"
              required
            />
          </div>
          <div>
            <Label>Total Units *</Label>
            <Input
              type="number"
              min="1"
              value={formData.units}
              onChange={e => setFormData({...formData, units: parseInt(e.target.value) || 1})}
              required
            />
          </div>
        </div>

        <div>
          <Label>Address *</Label>
          <Input
            value={formData.address}
            onChange={e => setFormData({...formData, address: e.target.value})}
            placeholder="Full address"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label>Postcode *</Label>
            <Input
              value={formData.postcode}
              onChange={e => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
              placeholder="e.g., FY1 2AA"
              required
            />
          </div>
          <div>
            <Label>Region</Label>
            <Input
              value={formData.region}
              onChange={e => setFormData({...formData, region: e.target.value})}
              placeholder="e.g., North West"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium mb-3 text-sm">Site Manager (Optional)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              value={formData.site_manager_name}
              onChange={e => setFormData({...formData, site_manager_name: e.target.value})}
              placeholder="Name"
            />
            <Input
              type="email"
              value={formData.site_manager_email}
              onChange={e => setFormData({...formData, site_manager_email: e.target.value})}
              placeholder="Email"
            />
            <Input
              value={formData.site_manager_phone}
              onChange={e => setFormData({...formData, site_manager_phone: e.target.value})}
              placeholder="Phone"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => setShowNewSite(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button type="submit" className="w-full sm:w-auto">Create Site</Button>
        </div>
      </form>
    );
  };

  // New Contract Form Component
  const NewContractForm = () => {
    const secureAPI = getSecureAPI();
    const [formData, setFormData] = useState({
      site_id: '',
      client_id: '',
      tier_id: 'STANDARD',
      units: 1,
      price_per_unit: 350,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      billing_frequency: 'annual',
      auto_renew: true,
      payment_terms_days: 30,
    });
    const [preview, setPreview] = useState(null);

    const updatePreview = async () => {
      if (formData.units && formData.tier_id && formData.price_per_unit) {
        try {
          const res = await axios.post(`${secureAPI}/api/contracts/calculate`, null, {
            params: {
              units: formData.units,
              tier_id: formData.tier_id,
              price_per_unit: formData.price_per_unit
            },
            headers
          });
          setPreview(res.data.financials);
        } catch (err) {}
      }
    };

    useEffect(() => {
      updatePreview();
    }, [formData.units, formData.tier_id, formData.price_per_unit]);

    const handleSiteSelect = (siteId) => {
      const site = sites.find(s => s.id === siteId);
      if (site) {
        setFormData({
          ...formData,
          site_id: siteId,
          client_id: site.client_id,
          units: site.units
        });
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await axios.post(`${secureAPI}/api/contracts`, formData, { headers });
        toast.success('Contract created successfully');
        setShowNewContract(false);
        loadDashboard();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create contract');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Site *</Label>
          <Select value={formData.site_id} onValueChange={handleSiteSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name} ({s.units} units)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label>Contract Tier *</Label>
            <Select value={formData.tier_id} onValueChange={v => {
              const tier = tiers.find(t => t.id === v);
              setFormData({
                ...formData,
                tier_id: v,
                price_per_unit: tier?.price_per_unit_default || formData.price_per_unit
              });
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tiers.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Units *</Label>
            <Input
              type="number"
              min="1"
              value={formData.units}
              onChange={e => setFormData({...formData, units: parseInt(e.target.value) || 1})}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label>Price Per Unit (£) *</Label>
            <Input
              type="number"
              min="1"
              value={formData.price_per_unit}
              onChange={e => setFormData({...formData, price_per_unit: parseFloat(e.target.value) || 0})}
              required
            />
          </div>
          <div>
            <Label>Billing Frequency</Label>
            <Select value={formData.billing_frequency} onValueChange={v => setFormData({...formData, billing_frequency: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label>Start Date *</Label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={e => setFormData({...formData, start_date: e.target.value})}
              required
            />
          </div>
          <div>
            <Label>End Date *</Label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={e => setFormData({...formData, end_date: e.target.value})}
              required
            />
          </div>
        </div>

        {/* Financial Preview */}
        {preview && (
          <div className="bg-slate-50 p-3 sm:p-4 rounded-lg border">
            <h4 className="font-medium mb-3 text-sm">Financial Preview</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
              <div>
                <p className="text-lg sm:text-2xl font-bold text-[#1F7A78]">£{preview.revenue.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Revenue</p>
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-slate-600">£{preview.estimated_cost.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Cost</p>
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-emerald-600">£{preview.profit.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Profit</p>
              </div>
              <div>
                <p className={`text-lg sm:text-2xl font-bold ${MARGIN_STATUS_COLORS[preview.margin_status]}`}>
                  {preview.margin_percent}%
                </p>
                <p className="text-[10px] sm:text-xs text-slate-500">Margin</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => setShowNewContract(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button type="submit" className="bg-[#1F7A78] hover:bg-[#005a5c] w-full sm:w-auto">Create Contract</Button>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Contracts System...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F7A78] to-[#0dbdad] text-white p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Contract Pricing System</h1>
              <p className="text-white/80 text-xs sm:text-sm">Sellable Structure - Margin Tracking - Auto Renewals</p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Dialog open={showNewSite} onOpenChange={setShowNewSite}>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="flex-1 sm:flex-none text-xs sm:text-sm">
                    <MapPin className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Add Site</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Site</DialogTitle>
                    <DialogDescription>Add a park or property collection for contract management.</DialogDescription>
                  </DialogHeader>
                  <NewSiteForm />
                </DialogContent>
              </Dialog>
              <Dialog open={showNewContract} onOpenChange={setShowNewContract}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#1F7A78] hover:bg-white/90 flex-1 sm:flex-none text-xs sm:text-sm">
                    <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">New Contract</span><span className="sm:hidden">New</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Contract</DialogTitle>
                    <DialogDescription>Set up a new site maintenance contract.</DialogDescription>
                  </DialogHeader>
                  <NewContractForm />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-50 rounded-lg">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.contracts?.active || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Contracts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-50 rounded-lg">
                  <PoundSterling className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">£{((dashboard?.financials?.total_revenue || 0) / 1000).toFixed(0)}k</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-50 rounded-lg">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">£{((dashboard?.financials?.total_actual_profit || 0) / 1000).toFixed(0)}k</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Profit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-50 rounded-lg">
                  <PieChart className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                </div>
                <div>
                  <p className={`text-lg sm:text-2xl font-bold ${
                    (dashboard?.financials?.overall_actual_margin || 0) >= 25 ? 'text-emerald-600' :
                    (dashboard?.financials?.overall_actual_margin || 0) >= 15 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {dashboard?.financials?.overall_actual_margin || 0}%
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Margin</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
                  <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.portfolio?.total_sites || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Sites</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-cyan-50 rounded-lg">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.portfolio?.total_units || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Units</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {((dashboard?.alerts?.margin_critical || 0) > 0 || (dashboard?.alerts?.renewals_due || 0) > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {(dashboard?.alerts?.margin_critical || 0) > 0 && (
              <Card className="border-l-4 border-l-red-600 shadow-sm">
                <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                  <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 animate-pulse shrink-0" />
                  <div>
                    <p className="font-medium text-red-800 text-sm sm:text-base">Critical: {dashboard.alerts.margin_critical} contracts below 15% margin</p>
                    <p className="text-xs sm:text-sm text-red-600">Immediate review required</p>
                  </div>
                </CardContent>
              </Card>
            )}
            {(dashboard?.alerts?.renewals_due || 0) > 0 && (
              <Card className="border-l-4 border-l-amber-500 shadow-sm">
                <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600 shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800 text-sm sm:text-base">{dashboard.alerts.renewals_due} contracts expiring soon</p>
                    <p className="text-xs sm:text-sm text-amber-600">Within 30 days - action required</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Tier Cards & Calculator */}
          <div className="space-y-4 sm:space-y-6">
            {/* Contract Tiers */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg">Contract Tiers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6">
                {tiers.map(tier => (
                  <div key={tier.id} className={`p-3 sm:p-4 rounded-lg border-2 ${TIER_COLORS[tier.id]}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm sm:text-base">{tier.name.split(' ')[0]}</h4>
                      <span className="text-base sm:text-lg font-bold">£{tier.price_per_unit_min}-£{tier.price_per_unit_max}</span>
                    </div>
                    <p className="text-[10px] sm:text-xs mb-2">{tier.description}</p>
                    <div className="flex items-center justify-between text-[10px] sm:text-xs">
                      <span>Target: {(tier.target_margin * 100).toFixed(0)}%</span>
                      <span>Cost: £{tier.estimated_cost_per_unit}/unit</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Pricing Calculator */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-800 to-slate-900 text-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">Pricing Calculator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-white/70 text-xs">Units</Label>
                    <Input
                      type="number"
                      value={calculatorUnits}
                      onChange={e => setCalculatorUnits(parseInt(e.target.value) || 1)}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">Tier</Label>
                    <Select value={calculatorTier} onValueChange={setCalculatorTier}>
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ESSENTIAL">Essential</SelectItem>
                        <SelectItem value="STANDARD">Standard</SelectItem>
                        <SelectItem value="PREMIUM">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs">£/unit</Label>
                    <Input
                      type="number"
                      value={calculatorPrice}
                      onChange={e => setCalculatorPrice(parseFloat(e.target.value) || 0)}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>
                <Button onClick={calculateContract} variant="secondary" className="w-full">
                  Calculate
                </Button>
                {calculatorResult && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/20">
                    <div className="text-center">
                      <p className="text-xl font-bold text-emerald-400">£{calculatorResult.revenue.toLocaleString()}</p>
                      <p className="text-xs text-white/60">Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">£{calculatorResult.estimated_cost.toLocaleString()}</p>
                      <p className="text-xs text-white/60">Cost</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-400">£{calculatorResult.profit.toLocaleString()}</p>
                      <p className="text-xs text-white/60">Profit</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-xl font-bold ${
                        calculatorResult.margin_status === 'healthy' ? 'text-emerald-400' :
                        calculatorResult.margin_status === 'warning' ? 'text-amber-400' : 'text-red-400'
                      }`}>
                        {calculatorResult.margin_percent}%
                      </p>
                      <p className="text-xs text-white/60">Margin</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle & Right - Contracts & Renewals */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Tabs defaultValue="contracts">
              <TabsList className="mb-4 flex w-full overflow-x-auto hide-scrollbar">
                <TabsTrigger value="contracts" className="text-xs sm:text-sm flex-1">Contracts</TabsTrigger>
                <TabsTrigger value="sites" className="text-xs sm:text-sm flex-1">Sites</TabsTrigger>
                <TabsTrigger value="renewals" className="text-xs sm:text-sm flex-1">Renewals</TabsTrigger>
                <TabsTrigger value="by-tier" className="text-xs sm:text-sm flex-1">By Tier</TabsTrigger>
                <TabsTrigger value="markups" className="text-xs sm:text-sm flex-1">Markups</TabsTrigger>
              </TabsList>

              <TabsContent value="contracts">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2 px-3 sm:px-6">
                    <CardTitle className="text-base sm:text-lg">Active Contracts</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <ScrollArea className="h-[350px] sm:h-[400px]">
                      {contracts.length > 0 ? (
                        <div className="space-y-3">
                          {contracts.map(contract => (
                            <div key={contract.id} className="p-3 sm:p-4 border rounded-lg hover:bg-slate-50">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge className={`${TIER_COLORS[contract.tier_id]} text-xs`}>{contract.tier_id}</Badge>
                                  <span className="font-medium text-sm sm:text-base">{contract.site_name}</span>
                                </div>
                                <span className="text-xs sm:text-sm text-slate-500">{contract.contract_reference}</span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
                                <div>
                                  <p className="text-slate-500">Units</p>
                                  <p className="font-medium">{contract.units}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Revenue</p>
                                  <p className="font-medium text-emerald-600">£{contract.financials?.revenue?.toLocaleString() || 0}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Margin</p>
                                  <p className={`font-medium ${MARGIN_STATUS_COLORS[contract.financials?.margin_status || 'healthy']}`}>
                                    {contract.financials?.margin_percent || 0}%
                                  </p>
                                </div>
                                <div>
                                  <p className="text-slate-500">End Date</p>
                                  <p className="font-medium">{new Date(contract.end_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 sm:py-12 text-slate-500">
                          <FileText className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">No active contracts yet</p>
                          <Button className="mt-4" size="sm" onClick={() => setShowNewContract(true)}>
                            Create First Contract
                          </Button>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sites">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Sites Portfolio</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      {sites.length > 0 ? (
                        <div className="space-y-3">
                          {sites.map(site => (
                            <div key={site.id} className="p-4 border rounded-lg hover:bg-slate-50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">{site.name}</span>
                                <Badge variant="outline">{site.units} units</Badge>
                              </div>
                              <p className="text-sm text-slate-500 mb-2">{site.address}, {site.postcode}</p>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">{site.active_contracts || 0} active contracts</span>
                                {site.total_revenue > 0 && (
                                  <span className="text-emerald-600 font-medium">£{site.total_revenue.toLocaleString()} revenue</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500">
                          <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No sites added yet</p>
                          <Button className="mt-4" onClick={() => setShowNewSite(true)}>
                            Add First Site
                          </Button>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="renewals">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-500" />
                      Upcoming Renewals (60 days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      {renewals.length > 0 ? (
                        <div className="space-y-3">
                          {renewals.map(contract => (
                            <div key={contract.id} className="p-4 border rounded-lg hover:bg-slate-50 border-l-4 border-l-amber-500">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge className={TIER_COLORS[contract.tier_id]}>{contract.tier_id}</Badge>
                                  <span className="font-medium">{contract.site_name}</span>
                                </div>
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  {contract.days_until_expiry} days left
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">{contract.client_name}</span>
                                <span>Expires: {new Date(contract.end_date).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center justify-between mt-3">
                                <span className="text-sm text-emerald-600 font-medium">
                                  £{contract.financials?.revenue?.toLocaleString() || 0}/year
                                </span>
                                <Button size="sm" variant="outline">
                                  Renew <ArrowRight className="h-3 w-3 ml-1" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500">
                          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                          <p>No renewals due in the next 60 days</p>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="by-tier">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Portfolio by Tier</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {dashboard?.by_tier && Object.entries(dashboard.by_tier).map(([tier, data]) => (
                        <div key={tier} className={`p-4 rounded-lg border ${TIER_COLORS[tier]}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">{tier}</h4>
                              <Badge variant="outline">{data.count} contracts</Badge>
                            </div>
                            <span className="text-2xl font-bold">£{(data.revenue / 1000).toFixed(0)}k</span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <span>{data.units} total units</span>
                            <span>Avg: £{Math.round(data.revenue / data.units)}/unit</span>
                          </div>
                          <Progress 
                            value={(data.revenue / (dashboard?.financials?.total_revenue || 1)) * 100} 
                            className="mt-3 h-2"
                          />
                        </div>
                      ))}
                      {(!dashboard?.by_tier || Object.keys(dashboard.by_tier).length === 0) && (
                        <div className="text-center py-12 text-slate-500">
                          <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No tier data available yet</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Client Markups Tab */}
              <TabsContent value="markups">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Percent className="h-5 w-5 text-[#1F7A78]" />
                          Client Markup Settings
                        </CardTitle>
                        <CardDescription>
                          Configure markup percentages per client. Default markup: <span className="font-semibold text-[#1F7A78]">{defaultMarkup}%</span>
                        </CardDescription>
                      </div>
                      <Dialog open={showAddMarkup} onOpenChange={setShowAddMarkup}>
                        <DialogTrigger asChild>
                          <Button className="bg-[#1F7A78] hover:bg-[#005f5f]">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Client Markup
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Client Markup</DialogTitle>
                            <DialogDescription>
                              Set a custom markup percentage for a specific client
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label>Client Name</Label>
                              <Input 
                                placeholder="e.g., Wyldecrest Parks"
                                value={newMarkupClient}
                                onChange={(e) => setNewMarkupClient(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Markup Percentage</Label>
                              <div className="flex items-center gap-2">
                                <Input 
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.5"
                                  value={newMarkupPercent}
                                  onChange={(e) => setNewMarkupPercent(parseFloat(e.target.value) || 0)}
                                  className="w-24"
                                />
                                <span className="text-slate-500">%</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">Set to 0 for no markup</p>
                            </div>
                            <div>
                              <Label>Notes (Optional)</Label>
                              <Input 
                                placeholder="e.g., Key client - special rate"
                                value={newMarkupNotes}
                                onChange={(e) => setNewMarkupNotes(e.target.value)}
                              />
                            </div>
                            <Button onClick={handleAddMarkup} className="w-full bg-[#1F7A78] hover:bg-[#005f5f]">
                              Add Markup Setting
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Default Markup Info */}
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-200 rounded-lg">
                              <Percent className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-700">Default Markup (All Other Clients)</p>
                              <p className="text-sm text-slate-500">Applied when no custom markup is set</p>
                            </div>
                          </div>
                          <div className="text-2xl font-bold text-slate-700">{defaultMarkup}%</div>
                        </div>
                      </div>

                      {/* Custom Markups List */}
                      <div className="border rounded-lg divide-y">
                        {markups.map((markup) => (
                          <div key={markup.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${markup.markup_percent === 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                                <Building2 className={`h-5 w-5 ${markup.markup_percent === 0 ? 'text-emerald-600' : 'text-amber-600'}`} />
                              </div>
                              <div>
                                <p className="font-medium">{markup.client_name}</p>
                                {markup.notes && (
                                  <p className="text-sm text-slate-500">{markup.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {editingMarkup === markup.id ? (
                                <div className="flex items-center gap-2">
                                  <Input 
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    defaultValue={markup.markup_percent}
                                    className="w-20"
                                    id={`markup-input-${markup.id}`}
                                  />
                                  <span>%</span>
                                  <Button 
                                    size="sm" 
                                    className="bg-[#1F7A78]"
                                    onClick={() => {
                                      const input = document.getElementById(`markup-input-${markup.id}`);
                                      handleUpdateMarkup(markup.id, input.value);
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingMarkup(null)}>
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <div className={`text-xl font-bold ${markup.markup_percent === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {markup.markup_percent}%
                                  </div>
                                  <Badge className={markup.markup_percent === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                    {markup.markup_percent === 0 ? 'No Markup' : 'Custom'}
                                  </Badge>
                                  <div className="flex gap-1">
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => setEditingMarkup(markup.id)}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="text-red-500 hover:text-red-600"
                                      onClick={() => handleDeleteMarkup(markup.id, markup.client_name)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {markups.length === 0 && (
                          <div className="p-8 text-center text-slate-500">
                            <Percent className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p className="font-medium">No custom markups configured</p>
                            <p className="text-sm">All clients will use the default {defaultMarkup}% markup</p>
                          </div>
                        )}
                      </div>

                      {/* Markup Example Calculator */}
                      <Card className="border bg-gradient-to-r from-[#1F7A78]/5 to-[#1F7A78]/10">
                        <CardContent className="p-4">
                          <h4 className="font-medium mb-3">Markup Example</h4>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="p-3 bg-white rounded-lg">
                              <p className="text-sm text-slate-500">Contractor Cost</p>
                              <p className="text-xl font-bold">£100.00</p>
                            </div>
                            <div className="p-3 bg-white rounded-lg">
                              <p className="text-sm text-slate-500">Wyldecrest (0%)</p>
                              <p className="text-xl font-bold text-emerald-600">£100.00</p>
                            </div>
                            <div className="p-3 bg-white rounded-lg">
                              <p className="text-sm text-slate-500">Other Client ({defaultMarkup}%)</p>
                              <p className="text-xl font-bold text-amber-600">£{(100 * (1 + defaultMarkup/100)).toFixed(2)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
